import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Target,
  Calendar,
  Image,
  Building,
  Briefcase,
  Users,
  TrendingUp,
  Eye,
  Edit,
  Trash2,
  Plus,
  Download,
  Search,
  Globe,
  LayoutDashboard,
  RefreshCw,
  Clock
} from 'lucide-react';
import { focusAreaService } from './focus-area/focusAreaService';
import { slideService } from './slide/slideService';
import { eventService } from './event/eventService';
import { partnerService } from './partner/partnerService';
import { donorService } from './donor/donorService';
import { departmentService } from './department/departmentService';

interface StatsCard {
  title: string;
  count: number;
  icon: React.ReactNode;
  color: string;
  path: string;
  loading: boolean;
}

interface RecentActivity {
  id: number;
  action: string;
  component: string;
  time: string;
  user: string;
}

// Helper function to safely get total from response
const getTotalFromResponse = (response: any): number => {
  if (!response) return 0;
  
  // Check if it's a paginated response with meta
  if (response.meta?.total !== undefined) {
    return response.meta.total;
  }
  
  // Check if it's a direct data array
  if (Array.isArray(response.data)) {
    return response.data.length;
  }
  
  // Check if it's a plain array
  if (Array.isArray(response)) {
    return response.length;
  }
  
  // Check if it's a success response with data
  if (response.success && Array.isArray(response.data)) {
    return response.data.length;
  }
  
  return 0;
};

const DashboardHome: React.FC = () => {
  const [stats, setStats] = useState<StatsCard[]>([
    { 
      title: 'Focus Areas', 
      count: 0, 
      icon: <Target size={24} />, 
      color: 'bg-green-500', 
      path: '/admin/home/focus-areas',
      loading: true 
    },
    { 
      title: 'Slides', 
      count: 0, 
      icon: <Image size={24} />, 
      color: 'bg-blue-500', 
      path: '/admin/home/slides',
      loading: true 
    },
    { 
      title: 'Events', 
      count: 0, 
      icon: <Calendar size={24} />, 
      color: 'bg-orange-500', 
      path: '/admin/home/events',
      loading: true 
    },
    { 
      title: 'Departments', 
      count: 0, 
      icon: <Building size={24} />, 
      color: 'bg-purple-500', 
      path: '/admin/departments',
      loading: true 
    },
    { 
      title: 'Partners', 
      count: 0, 
      icon: <Users size={24} />, 
      color: 'bg-indigo-500', 
      path: '/admin/partners',
      loading: true 
    },
    { 
      title: 'Donors', 
      count: 0, 
      icon: <Globe size={24} />, 
      color: 'bg-pink-500', 
      path: '/admin/donors',
      loading: true 
    },
    // { 
    //   title: 'Projects', 
    //   count: 0, 
    //   icon: <Briefcase size={24} />, 
    //   color: 'bg-red-500', 
    //   path: '/admin/projects',
    //   loading: true 
    // },
    // { 
    //   title: 'Jobs', 
    //   count: 0, 
    //   icon: <Briefcase size={24} />, 
    //   color: 'bg-yellow-500', 
    //   path: '/admin/jobs',
    //   loading: true 
    // }
  ]);

  const [recentActivities] = useState<RecentActivity[]>([
    { id: 1, action: 'Created', component: 'Focus Area: Quality Education', time: '2 hours ago', user: 'Admin' },
    { id: 2, action: 'Updated', component: 'Focus Area: Enhance Health', time: '4 hours ago', user: 'Admin' },
    { id: 3, action: 'Viewed', component: 'Dashboard Analytics', time: '6 hours ago', user: 'Admin' },
    { id: 4, action: 'Deleted', component: 'Expired Job Posting', time: '1 day ago', user: 'Admin' },
    { id: 5, action: 'Created', component: 'New Event: Workshop', time: '2 days ago', user: 'Admin' }
  ]);

  const [searchTerm, setSearchTerm] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [totalItems, setTotalItems] = useState(0);
  const [lastUpdated, setLastUpdated] = useState<string>('');

  // Fetch stats from API
  const fetchAllStats = async () => {
    try {
      setRefreshing(true);
      
      // Fetch all data in parallel
      const [
        slidesRes,
        focusAreasRes,
        eventsRes,
        departmentsRes,
        partnersRes,
        donorsRes
      ] = await Promise.allSettled([
        slideService.getAll({ per_page: 1 }).catch(err => {
          console.log('Slides fetch failed:', err.message);
          return { success: true, data: [] };
        }),
        focusAreaService.getAll({ per_page: 1 }).catch(err => {
          console.log('Focus areas fetch failed:', err.message);
          return { success: false, data: [] };
        }),
        eventService.getAll({ per_page: 1 }).catch(err => {
          console.log('Events fetch failed:', err.message);
          return { success: false, data: [] };
        }),
        departmentService.getAll({ per_page: 1 }).catch(err => {
          console.log('Departments fetch failed:', err.message);
          return { success: false, data: [] };
        }),
        partnerService.getAll({ per_page: 1 }).catch(err => {
          console.log('Partners fetch failed:', err.message);
          return { success: false, data: [] };
        }),
        donorService.getAll({ per_page: 1 }).catch(err => {
          console.log('Donors fetch failed:', err.message);
          return { success: false, data: [] };
        })
      ]);

      const newStats = [...stats];
      let total = 0;

      // Process Slides
      if (slidesRes.status === 'fulfilled') {
        const value = slidesRes.value;
        const slidesTotal = getTotalFromResponse(value);
        newStats[1] = {
          ...newStats[1],
          count: slidesTotal,
          loading: false
        };
        total += slidesTotal;
      }

      // Process Focus Areas
      if (focusAreasRes.status === 'fulfilled') {
        const value = focusAreasRes.value;
        const focusTotal = getTotalFromResponse(value);
        newStats[0] = {
          ...newStats[0],
          count: focusTotal,
          loading: false
        };
        total += focusTotal;
      }

      // Process Events
      if (eventsRes.status === 'fulfilled') {
        const value = eventsRes.value;
        const eventsTotal = getTotalFromResponse(value);
        newStats[2] = {
          ...newStats[2],
          count: eventsTotal,
          loading: false
        };
        total += eventsTotal;
      }

      // Process Departments
      if (departmentsRes.status === 'fulfilled') {
        const value = departmentsRes.value;
        const deptTotal = getTotalFromResponse(value);
        newStats[3] = {
          ...newStats[3],
          count: deptTotal,
          loading: false
        };
        total += deptTotal;
      }

      // Process Partners
      if (partnersRes.status === 'fulfilled') {
        const value = partnersRes.value;
        const partnerTotal = getTotalFromResponse(value);
        newStats[4] = {
          ...newStats[4],
          count: partnerTotal,
          loading: false
        };
        total += partnerTotal;
      }

      // Process Donors
      if (donorsRes.status === 'fulfilled') {
        const value = donorsRes.value;
        const donorTotal = getTotalFromResponse(value);
        newStats[5] = {
          ...newStats[5],
          count: donorTotal,
          loading: false
        };
        total += donorTotal;
      }

      setStats(newStats);
      setTotalItems(total);
      setLastUpdated(new Date().toLocaleString());
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchAllStats();
  }, []);

  const handleRefresh = () => {
    fetchAllStats();
  };

  const filteredActivities = recentActivities.filter(activity =>
    activity.component.toLowerCase().includes(searchTerm.toLowerCase()) ||
    activity.action.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6">
      {/* Welcome Card with Refresh */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold mb-2">Welcome back, Admin! 👋</h1>
            <p className="text-blue-100">Here's what's happening with your components today.</p>
            {lastUpdated && (
              <p className="text-sm text-blue-200 mt-2">
                <Clock size={14} className="inline mr-1" />
                Last updated: {lastUpdated}
              </p>
            )}
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50"
            >
              <RefreshCw size={18} className={refreshing ? 'animate-spin' : ''} />
              <span>{refreshing ? 'Refreshing...' : 'Refresh'}</span>
            </button>
            <div className="hidden md:block">
              <div className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center">
                <TrendingUp size={32} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {stats.map((stat, index) => (
          <Link
            key={index}
            to={stat.path}
            className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 hover:shadow-md transition-all hover:scale-105"
          >
            <div className="flex items-center justify-between mb-4">
              <div className={`${stat.color} w-12 h-12 rounded-lg flex items-center justify-center`}>
                <div className="text-white">
                  {stat.icon}
                </div>
              </div>
              {stat.loading ? (
                <div className="w-16 h-8 bg-gray-200 animate-pulse rounded"></div>
              ) : (
                <span className="text-3xl font-bold text-gray-800">{stat.count}</span>
              )}
            </div>
            
            <h3 className="text-lg font-semibold text-gray-800">{stat.title}</h3>
          </Link>
        ))}
      </div>

      {/* Quick Stats Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-gray-600">Total Items</h3>
            <LayoutDashboard size={20} className="text-blue-600" />
          </div>
          <p className="text-3xl font-bold text-gray-800">{totalItems}</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-gray-600">Categories</h3>
            <Briefcase size={20} className="text-purple-600" />
          </div>
          <p className="text-3xl font-bold text-purple-600">
            {stats.filter(s => !s.loading && s.count > 0).length}
          </p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-gray-600">With Content</h3>
            <TrendingUp size={20} className="text-green-600" />
          </div>
          <p className="text-3xl font-bold text-green-600">
            {stats.filter(s => !s.loading && s.count > 0).length}
          </p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-gray-600">Empty</h3>
            <TrendingUp size={20} className="text-red-600" />
          </div>
          <p className="text-3xl font-bold text-red-600">
            {stats.filter(s => !s.loading && s.count === 0).length}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Quick Actions */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-800">Quick Actions</h2>
              <div className="flex space-x-2">
                <button className="px-4 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors flex items-center space-x-2">
                  <Plus size={16} />
                  <span>Add New</span>
                </button>
                <button className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex items-center space-x-2">
                  <Download size={16} />
                  <span>Export</span>
                </button>
              </div>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { label: 'New Focus Area', icon: <Target size={20} />, path: '/admin/home/focus-areas/create', color: 'green' },
                { label: 'Upload Slides', icon: <Image size={20} />, path: '/admin/home/slides/create', color: 'blue' },
                { label: 'Add Event', icon: <Calendar size={20} />, path: '/admin/home/events/create', color: 'orange' },
                { label: 'New Department', icon: <Building size={20} />, path: '/admin/departments/create', color: 'purple' },
                { label: 'Add Partner', icon: <Users size={20} />, path: '/admin/partners/create', color: 'indigo' },
                { label: 'Add Donor', icon: <Globe size={20} />, path: '/admin/donors/create', color: 'pink' },
                { label: 'Create Project', icon: <Briefcase size={20} />, path: '/admin/projects/create', color: 'red' },
                { label: 'Post Job', icon: <Briefcase size={20} />, path: '/admin/jobs/create', color: 'yellow' }
              ].map((action, index) => (
                <Link
                  key={index}
                  to={action.path}
                  className={`bg-${action.color}-50 hover:bg-${action.color}-100 border border-${action.color}-200 rounded-lg p-4 flex flex-col items-center justify-center space-y-2 transition-colors group`}
                >
                  <div className={`w-10 h-10 bg-white rounded-lg flex items-center justify-center group-hover:bg-${action.color}-100 transition-colors`}>
                    <div className={`text-${action.color}-600`}>
                      {action.icon}
                    </div>
                  </div>
                  <span className={`text-sm text-${action.color}-700 text-center`}>{action.label}</span>
                </Link>
              ))}
            </div>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-gray-800">Recent Activity</h2>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
              <input
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
          
          <div className="space-y-4">
            {filteredActivities.map((activity) => (
              <div key={activity.id} className="flex items-start space-x-4 p-3 hover:bg-gray-50 rounded-lg transition-colors">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  activity.action === 'Created' ? 'bg-green-100 text-green-600' :
                  activity.action === 'Updated' ? 'bg-blue-100 text-blue-600' :
                  activity.action === 'Deleted' ? 'bg-red-100 text-red-600' :
                  'bg-gray-100 text-gray-600'
                }`}>
                  {activity.action === 'Created' ? <Plus size={18} /> :
                   activity.action === 'Updated' ? <Edit size={18} /> :
                   activity.action === 'Deleted' ? <Trash2 size={18} /> :
                   <Eye size={18} />}
                </div>
                <div className="flex-1">
                  <div className="flex justify-between">
                    <p className="font-medium text-gray-800">{activity.component}</p>
                    <span className="text-sm text-gray-500">{activity.time}</span>
                  </div>
                  <p className="text-sm text-gray-600">
                    {activity.action} by {activity.user}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* System Status */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h2 className="text-xl font-bold text-gray-800 mb-6">System Status</h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-5 rounded-xl border border-green-100">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-green-800">API Status</h3>
              <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
            </div>
            <p className="text-green-700">All systems operational</p>
          </div>
          <div className="bg-gradient-to-br from-blue-50 to-cyan-50 p-5 rounded-xl border border-blue-100">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-blue-800">Database</h3>
              <div className="text-sm font-medium text-blue-600">Connected</div>
            </div>
            <p className="text-blue-700">Laravel backend active</p>
          </div>
          <div className="bg-gradient-to-br from-purple-50 to-violet-50 p-5 rounded-xl border border-purple-100">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-purple-800">Storage</h3>
              <div className="text-sm font-medium text-purple-600">{totalItems} files</div>
            </div>
            <p className="text-purple-700">Images stored in public disk</p>
          </div>
          <div className="bg-gradient-to-br from-indigo-50 to-blue-50 p-5 rounded-xl border border-indigo-100">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-indigo-800">Last Sync</h3>
              <div className="text-sm font-medium text-indigo-600">Now</div>
            </div>
            <p className="text-indigo-700">Real-time updates active</p>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="text-center text-sm text-gray-500">
        <p>All statistics are fetched from your Laravel backend in real-time</p>
      </div>
    </div>
  );
};

export default DashboardHome;